/**
 * Cmessagemodal Component
 *
 * A high-priority modal for displaying critical messages, errors, or confirmations.
 * Features a distinctive triangle icon, hacker-style header, and temper-based styling.
 * Renders above all other content including standard modals.
 */

'use client';

import { useCallback, useRef, type ReactElement, type ReactNode } from 'react';
import { ButtonPreset } from '@/constants/componentPresets';
import { Temper } from '@/types/frontend-types';
import type { DataElScope } from '@/utils/dataEl';
import { MessageModalFrame } from './MessageModalFrame';
import { MessageModalPanel } from './MessageModalPanel';
import { MESSAGE_MODAL_VARIANT, type MessageModalVariant } from './messageModalConstants';
import { useMessageModalActions } from './useMessageModalActions';
import { useMessageModalLayoutModel } from './useMessageModalLayoutModel';
import { useMessageModalScrollDimensions } from './useMessageModalScrollDimensions';

export interface CmessagemodalProps {
  /** The component rendering this modal, e.g. "MessageModalProvider/Modal". Required: it is
   *  what makes an inspected node say WHICH modal it belongs to. */
  dataElScope: DataElScope;
  visible: boolean;
  temper: Temper;
  title: string;
  titleNode?: ReactNode;
  hackerAllCaps?: boolean;
  body?: string; // Markdown supported
  children?: ReactNode; // Custom inner component
  footerNote?: string;
  primaryButtonText?: string;
  primaryButtonPreset?: ButtonPreset;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  onClose: () => void;
  disableDismiss?: boolean;
  closeOnPrimary?: boolean;
  closeOnSecondary?: boolean;
  loading?: boolean;
  /** Locks only the primary action while leaving Cancel/Escape/backdrop usable. */
  primaryLoading?: boolean;
  variant?: MessageModalVariant;
}

export function Cmessagemodal({
  dataElScope,
  visible,
  temper,
  title,
  titleNode,
  hackerAllCaps = false,
  body,
  children,
  footerNote,
  primaryButtonText,
  primaryButtonPreset = ButtonPreset.Primary,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  onClose,
  disableDismiss = false,
  closeOnPrimary = true,
  closeOnSecondary = true,
  loading = false,
  primaryLoading = false,
  variant = MESSAGE_MODAL_VARIANT.Global,
}: CmessagemodalProps): ReactElement {
  const rootRef = useRef<HTMLDivElement>(null);
  const layoutModel = useMessageModalLayoutModel({
    temper,
    primaryButtonText,
    secondaryButtonText,
    variant,
  });
  const { handlePrimary, handleSecondary } = useMessageModalActions({
    loading,
    primaryLoading,
    closeOnPrimary,
    closeOnSecondary,
    onClose,
    onPrimaryClick,
    onSecondaryClick,
  });
  const handleBackdropClick = useCallback((): void => {
    if (!disableDismiss && !loading) {
      onClose();
    }
  }, [disableDismiss, loading, onClose]);

  useMessageModalScrollDimensions({
    visible,
    isMobile: layoutModel.isMobile,
    vw: layoutModel.vw,
    vh: layoutModel.vh,
    rootRef,
  });

  return (
    <MessageModalFrame
      dataElScope={dataElScope}
      visible={visible}
      loaded={layoutModel.loaded}
      reduceMotion={layoutModel.reduceMotion}
      rootRef={rootRef}
      rootStyle={layoutModel.messageModalRootStyle}
      containerStyle={layoutModel.messageModalContainerStyle}
      onBackdropClick={handleBackdropClick}
    >
      <MessageModalPanel
        dataElScope={dataElScope}
        loading={loading}
        primaryLoading={primaryLoading}
        reduceMotion={layoutModel.reduceMotion}
        temper={temper}
        title={title}
        titleNode={titleNode}
        hackerAllCaps={hackerAllCaps}
        body={body}
        footerNote={footerNote}
        primaryButtonText={primaryButtonText}
        primaryButtonPreset={primaryButtonPreset}
        secondaryButtonText={secondaryButtonText}
        isMobile={layoutModel.isMobile}
        isGlobalLayout={layoutModel.isGlobalLayout}
        footerButtonStyle={layoutModel.footerButtonStyle}
        messageModalFooterStyle={layoutModel.messageModalFooterStyle}
        messageModalFooterButtonPadding={layoutModel.messageModalFooterButtonPadding}
        messageModalFooterButtonHeight={layoutModel.messageModalFooterButtonHeight}
        messageModalSurfaceBoxShadow={layoutModel.messageModalSurfaceBoxShadow}
        messageModalSurfaceStyle={layoutModel.messageModalSurfaceStyle}
        messageModalContentAreaStyle={layoutModel.messageModalContentAreaStyle}
        messageModalContentAreaHeight={layoutModel.messageModalContentAreaHeight}
        messageModalSurfaceHeight={layoutModel.messageModalSurfaceHeight}
        messageModalSurfacePadding={layoutModel.messageModalSurfacePadding}
        messageModalSurfaceJustifyContent={layoutModel.messageModalSurfaceJustifyContent}
        onPrimaryClick={handlePrimary}
        onSecondaryClick={handleSecondary}
      >
        {children}
      </MessageModalPanel>
    </MessageModalFrame>
  );
}
